// views/Dashboard/Overview/components/EarningsChart/EarningsChart.tsx
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  type TooltipProps,
} from 'recharts';
import Loading from '../../../../../components/common/Loading/Loading';
import { BonusHistoryObj, Transaction } from '../../../../../Entities';
import { notify } from '../../../../../components/common/Toast/toastBridge';
import styles from './EarningsChart.module.css';

type ChartType = 'earnings' | 'cumulative_earnings' | 'bonuses' | 'success';
type ChartPeriod = 'half_year' | 'year';

interface ChartRow {
  month: string;
  bonusEarnings: number;
  referralEarnings: number;
  total: number;
  success: number;
  bonuses: number;
}

const TITLES: Record<ChartType, string> = {
  earnings: 'Měsíční výdělky',
  cumulative_earnings: 'Kumulativní výdělky',
  bonuses: 'Počet dokončených bonusů',
  success: 'Naplnění potenciálu bonusů',
};

const UNIT: Record<ChartType, string> = {
  earnings: ' Kč',
  cumulative_earnings: ' Kč',
  bonuses: '',
  success: ' %',
};

const czk = (n: number) => Math.round(n).toLocaleString('cs-CZ');

const CustomTooltip =
  (type: ChartType) =>
  ({ active, payload, label }: TooltipProps<number, string>) => {
    if (!active || !payload?.length) return null;
    return (
      <div className={styles.tooltip}>
        <div className={styles.tooltipMonth}>{label}</div>
        {payload.map((p) => (
          <div key={p.name} className={styles.tooltipRow}>
            <span className={styles.tooltipDot} style={{ background: String(p.color) }} />
            <span>{p.name}</span>
            <strong>
              {czk(Number(p.value))}
              {UNIT[type]}
            </strong>
          </div>
        ))}
      </div>
    );
  };

const EarningsChart = () => {
  const [chartPeriod, setChartPeriod] = useState<ChartPeriod>('half_year');
  const [chartType, setChartType] = useState<ChartType>('earnings');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [rows, setRows] = useState<ChartRow[]>([]);

  const getStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);

      const bonusStats = await BonusHistoryObj.getStats(chartPeriod);
      if (!bonusStats) throw new Error('Bonus History stats loading failed');

      const referralStats = await Transaction.getStats(chartPeriod);
      if (!referralStats) throw new Error('Transaction stats loading failed');

      const n = Math.min(bonusStats.length, referralStats.length);
      const data: ChartRow[] = [];
      for (let i = 0; i < n; i++) {
        const b = bonusStats[i];
        const r = referralStats[i];
        data.push({
          month: b.month,
          bonusEarnings: b.reached_profit,
          referralEarnings: r.reached_profit,
          total: b.reached_profit + r.reached_profit,
          success: b.success_rate * 100,
          bonuses: b.bonuses,
        });
      }
      setRows(data);
    } catch (err) {
      console.error(err);
      notify.error('Nepodařilo se načíst statistiky grafu');
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [chartPeriod]);

  useEffect(() => {
    getStats();
  }, [getStats]);

  // Derived dataset (cumulative sums when needed).
  const data = useMemo(() => {
    if (chartType !== 'cumulative_earnings') return rows;
    let cumBonus = 0;
    let cumReferral = 0;
    return rows.map((row) => {
      cumBonus += row.bonusEarnings;
      cumReferral += row.referralEarnings;
      return { ...row, bonusEarnings: cumBonus, referralEarnings: cumReferral, total: cumBonus + cumReferral };
    });
  }, [rows, chartType]);

  const axis = { stroke: 'var(--ink-faint)', fontSize: 12 };
  const gridColor = 'var(--line)';

  const renderChart = () => {
    if (chartType === 'cumulative_earnings') {
      return (
        <AreaChart data={data} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
          <defs>
            <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--orange)" stopOpacity={0.35} />
              <stop offset="100%" stopColor="var(--orange)" stopOpacity={0.04} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
          <XAxis dataKey="month" tick={axis} tickLine={false} axisLine={{ stroke: gridColor }} />
          <YAxis tick={axis} tickLine={false} axisLine={false} width={54} />
          <Tooltip content={CustomTooltip(chartType)} />
          <Area type="monotone" dataKey="total" name="Celkem" stroke="var(--orange)" strokeWidth={2.5} fill="url(#areaFill)" />
        </AreaChart>
      );
    }

    if (chartType === 'success') {
      return (
        <LineChart data={data} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
          <XAxis dataKey="month" tick={axis} tickLine={false} axisLine={{ stroke: gridColor }} />
          <YAxis tick={axis} tickLine={false} axisLine={false} width={44} domain={[0, 100]} />
          <Tooltip content={CustomTooltip(chartType)} />
          <Line type="monotone" dataKey="success" name="Úspěšnost" stroke="var(--info)" strokeWidth={2.5} dot={{ r: 3 }} />
        </LineChart>
      );
    }

    if (chartType === 'bonuses') {
      return (
        <BarChart data={data} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
          <XAxis dataKey="month" tick={axis} tickLine={false} axisLine={{ stroke: gridColor }} />
          <YAxis tick={axis} tickLine={false} axisLine={false} width={40} allowDecimals={false} />
          <Tooltip content={CustomTooltip(chartType)} cursor={{ fill: 'var(--bg-warm)' }} />
          <Bar dataKey="bonuses" name="Bonusy" fill="var(--success)" radius={[6, 6, 0, 0]} maxBarSize={44} />
        </BarChart>
      );
    }

    // earnings — stacked bar (bonus + referral)
    return (
      <BarChart data={data} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
        <XAxis dataKey="month" tick={axis} tickLine={false} axisLine={{ stroke: gridColor }} />
        <YAxis tick={axis} tickLine={false} axisLine={false} width={54} />
        <Tooltip content={CustomTooltip(chartType)} cursor={{ fill: 'var(--bg-warm)' }} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="bonusEarnings" name="Bonusy" stackId="e" fill="var(--orange)" radius={[0, 0, 0, 0]} maxBarSize={44} />
        <Bar dataKey="referralEarnings" name="Doporučení" stackId="e" fill="var(--success)" radius={[6, 6, 0, 0]} maxBarSize={44} />
      </BarChart>
    );
  };

  return (
    <div className={styles.card} data-tour="earnings-chart">
      <div className={styles.header}>
        <h3 className={styles.title}>{TITLES[chartType]}</h3>
        <div className={styles.controls} data-tour="chart-controls">
          <select value={chartType} onChange={(e) => setChartType(e.target.value as ChartType)} className={styles.select}>
            <option value="earnings">Měsíční výdělky</option>
            <option value="cumulative_earnings">Kumulativní výdělky</option>
            <option value="bonuses">Bonusy</option>
            <option value="success">Naplnění potenciálu bonusů</option>
          </select>
          <select value={chartPeriod} onChange={(e) => setChartPeriod(e.target.value as ChartPeriod)} className={styles.select}>
            <option value="half_year">Posledních 6 měsíců</option>
            <option value="year">Celý rok</option>
          </select>
        </div>
      </div>

      <div className={styles.chartArea}>
        {loading ? (
          <Loading maxHeight={false} />
        ) : error ? (
          <div className={styles.errorState}>
            <p>Nepodařilo se načíst graf</p>
            <button className={styles.retry} onClick={getStats}>
              Zkusit znovu
            </button>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {renderChart()}
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default EarningsChart;
